import type { Logger } from '@feature-hub/logger';
import {
  getConsumptionsAndEmissionsWithIdentifiers,
  type VueFormatterServiceInterfaceV1,
  type LocaleServiceV1,
} from './feature-service-types';

type WltpEntry = { formattedConsumption?: unknown; formattedEmission?: unknown } | undefined;
type Slides = ContentApp['slides'];

type CaeMissingEvent = {
  event: 'CAE_MISSING';
  fa: typeof __FEATURE_APP_NAME__;
  carlineId: string;
  market: string;
  page: string;
  timestamp: string;
};

type WltpComplianceLogger = Pick<Logger, 'warn' | 'error'>;

const loggedCaeMissingEvents = new Set<string>();
const loggedComplianceResults = new Set<string>();

export const resetWltpComplianceLogCache = (): void => {
  loggedCaeMissingEvents.clear();
  loggedComplianceResults.clear();
};

const hasRequiredWltpValues = (entry: WltpEntry) => {
  const { formattedConsumption, formattedEmission } = entry || {};
  return Boolean(formattedConsumption && formattedEmission);
};

const getPagePath = (): string => {
  if (typeof window === 'undefined') return '';
  return window.location.pathname;
};

const buildCaeMissingMessage = (carlineId: string, localeService: LocaleServiceV1): string => {
  const payload: CaeMissingEvent = {
    event: 'CAE_MISSING',
    fa: __FEATURE_APP_NAME__,
    carlineId,
    market: localeService.market || localeService.countryCode || '',
    page: getPagePath(),
    timestamp: new Date().toISOString(),
  };
  return JSON.stringify(payload);
};

const shouldLogOnceInBrowser = (cache: Set<string>, key: string): boolean => {
  if (typeof window === 'undefined') return true;
  if (cache.has(key)) return false;
  cache.add(key);
  return true;
};

const logCaeMissing = (
  carlineId: string,
  localeService: LocaleServiceV1,
  logger?: WltpComplianceLogger,
): void => {
  const market = localeService.market || localeService.countryCode || '';
  const logKey = `${__FEATURE_APP_NAME__}:${market}:${getPagePath()}:${carlineId}`;
  if (!shouldLogOnceInBrowser(loggedCaeMissingEvents, logKey)) return;

  const errorMsg = buildCaeMissingMessage(carlineId, localeService);
  if (logger) {
    logger.error(errorMsg);
  } else {
    console.error(errorMsg);
  }
};

export const collectWltpKeys = (slides: Slides): string[] => {
  const allKeys = slides.flatMap((slide) => slide.slideWltpKeys ?? []).filter(Boolean);
  return [...new Set(allKeys)];
};

export const isWltpComplianceSatisfied = async (
  slides: Slides,
  vueFormatterService: VueFormatterServiceInterfaceV1,
  localeService: LocaleServiceV1,
) => {
  const keys = collectWltpKeys(slides);
  if (!keys.length) return true;

  try {
    const result = await getConsumptionsAndEmissionsWithIdentifiers(
      keys,
      vueFormatterService,
      localeService,
    );

    return keys.every((key) => hasRequiredWltpValues(result[key]));
  } catch {
    return false;
  }
};

export const getPerSlideWltpCompliance = async (
  slides: Slides,
  vueFormatterService: VueFormatterServiceInterfaceV1,
  localeService: LocaleServiceV1,
  logger?: WltpComplianceLogger,
): Promise<boolean[]> => {
  const perSlideCompliance = new Array<boolean>(slides.length).fill(true);
  const slideKeysByIndex: string[][] = Array.from({ length: slides.length }, () => []);
  const validKeySlideIndices: number[] = [];
  const allKeysSet = new Set<string>();

  slides.forEach((slide, index) => {
    const rawKeys = slide.slideWltpKeys ?? [];
    const slideKeys = rawKeys.filter(Boolean);
    slideKeysByIndex[index] = slideKeys;

    if (slideKeys.length > 0) {
      validKeySlideIndices.push(index);
      slideKeys.forEach((key) => allKeysSet.add(key));
    }
  });

  if (validKeySlideIndices.length === 0) {
    return perSlideCompliance;
  }

  let result: Record<string, WltpEntry> = {};
  try {
    const allKeys = [...allKeysSet];
    result = await getConsumptionsAndEmissionsWithIdentifiers(
      allKeys,
      vueFormatterService,
      localeService,
    );

    validKeySlideIndices.forEach((slideIndex) => {
      const slideKeys = slideKeysByIndex[slideIndex];
      let slideCompliance = true;

      slideKeys.forEach((key) => {
        if (!hasRequiredWltpValues(result[key])) {
          slideCompliance = false;
          logCaeMissing(key, localeService, logger);
        }
      });

      perSlideCompliance[slideIndex] = slideCompliance;
    });

    const complianceLogKey = JSON.stringify({
      allKeys,
      perSlideCompliance,
    });
    if (
      typeof window !== 'undefined' &&
      shouldLogOnceInBrowser(loggedComplianceResults, complianceLogKey)
    ) {
      console.log('WLTP Compliance:', {
        allKeys,
        result,
        perSlideCompliance,
      });
    }

    return perSlideCompliance;
  } catch {
    if (typeof window !== 'undefined') {
      console.warn('WLTP Compliance check failed');
    }

    validKeySlideIndices.forEach((slideIndex) => {
      slideKeysByIndex[slideIndex].forEach((key) => {
        logCaeMissing(key, localeService, logger);
      });
      perSlideCompliance[slideIndex] = false;
    });

    return perSlideCompliance;
  }
};
